import { useEffect, useState, type ReactNode } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import { Outlet, useLocation } from "react-router-dom";
import { SegmentedNav } from "../../../common/components/SegmentedNav";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { visuallyHidden } from "../../../common/visuallyHidden";

/** How long to wait for the profile before showing the compact layout anyway. */
export const RECOVERY_PROFILE_WAIT_MS = 4000;

const RECOVERY_DESTINATIONS = [
  { id: "home", label: "Home", to: "/" },
  { id: "tournaments", label: "Tournaments", to: "/compete/tournaments" },
  { id: "moderation", label: "Safety & appeals", to: "/me/moderation" },
  { id: "privacy", label: "Privacy & data", to: "/me/privacy" },
] as const;

/**
 * Safety, privacy and account recovery, reachable outside the profile gate so
 * a restricted player can still read their status, appeal and manage data.
 *
 * An active account sees these pages inside the normal app (`appShell`, passed
 * in by the router so this module never imports the app layer). Anything else
 * (restricted, no profile, or a profile that can't be read) gets this compact
 * layout with only the destinations it may use.
 */
export function AccountRecoveryLayout({ appShell }: { appShell?: ReactNode }) {
  const { user, signOut } = useAuth();
  const profile = useMyProfile(Boolean(user));
  const { pathname } = useLocation();
  // Recovery must never depend on the profile answering: after a short wait,
  // show the compact layout (the pages work without it).
  const [waitedEnough, setWaitedEnough] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setWaitedEnough(true), RECOVERY_PROFILE_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // Only before the first answer: a failed read may be retried later (a page
  // below mounts the same query), and that must not swap the page for a spinner.
  if (profile.isPending && !profile.isFetched && !waitedEnough) {
    return (
      <Stack
        component="main"
        role="status"
        aria-live="polite"
        sx={{ minHeight: "100dvh", alignItems: "center", justifyContent: "center", bgcolor: "background.default" }}
      >
        <CircularProgress aria-hidden="true" />
        <Box component="span" sx={visuallyHidden}>
          Loading…
        </Box>
      </Stack>
    );
  }

  if (appShell && profile.data?.account_status === "active") return <>{appShell}</>;

  const active =
    RECOVERY_DESTINATIONS.find((item) => item.to !== "/" && pathname.startsWith(item.to))?.id ?? "";

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <Box component="header" sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ maxWidth: 720, mx: "auto", px: 2, py: 1.5, alignItems: "center" }}
        >
          <Typography sx={{ flex: 1, fontWeight: 700 }}>Badminton Warriors</Typography>
          <Button color="inherit" size="small" onClick={() => void signOut()}>
            Sign out
          </Button>
        </Stack>
      </Box>
      <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto", px: 2, py: 3 }}>
        <SegmentedNav
          label="Available account destinations"
          items={RECOVERY_DESTINATIONS}
          active={active}
          wrap
        />
        <Box component="main">
          <Outlet />
        </Box>
      </Stack>
    </Box>
  );
}
